import * as React from 'react';

import { UploadIcon, AlertCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

type EmptyStateProps = {
  /**
   * Current upload status
   */
  status?: 'idle' | 'uploading' | 'error';

  /**
   * Whether drag is currently active
   */
  isDragActive?: boolean;

  /**
   * Accepted file extensions for display
   */
  acceptedExtensions: string[];

  /**
   * Maximum file size in MB
   */
  maxSizeMB: number;

  /**
   * Whether to list the accepted file extensions
   */
  showAcceptedFormats?: boolean;

  /**
   * Custom placeholder text
   */
  placeholder?: string;

  /**
   * Number of files selected (for status text)
   */
  fileCount?: number;

  /**
   * Whether the component is disabled
   */
  disabled?: boolean;

  /**
   * Additional className for the container
   */
  className?: string;

  /**
   * Children to render (input element)
   */
  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>;

const EmptyState = ({
  status = 'idle',
  isDragActive = false,
  acceptedExtensions,
  maxSizeMB,
  showAcceptedFormats = true,
  placeholder,
  fileCount = 0,
  disabled = false,
  className,
  children,
  ref,
  ...rest
}: EmptyStateProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { t } = useTranslation();

  const getMainIcon = () => {
    if (status === 'error') {
      return <AlertCircleIcon size={16} className="text-destructive" />;
    }

    return <UploadIcon size={16} className="text-foreground" />;
  };

  const hasFiles = fileCount > 0;

  const getMainText = () => {
    if (status === 'uploading') {
      return t('file_upload.uploading');
    }
    if (hasFiles) {
      return `${fileCount} ${t('file_upload.files_selected', { count: fileCount })}`;
    }
    return placeholder || t('file_upload.dnd_files');
  };

  const shouldShowBrowseLink = !hasFiles && status === 'idle';

  const isUploading = status === 'uploading';

  return (
    <div className="min-w-39">
      <div
        ref={ref}
        {...rest}
        className={cn(
          'relative flex min-h-33 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed p-8 transition-colors',
          {
            // Disabled state
            'pointer-events-none opacity-50': disabled || isUploading,

            // Drag active state
            'border-accent bg-highlight-background': isDragActive,

            // Default/hover states
            'hover:bg-highlight-background': !disabled && !isUploading && !isDragActive,
            'border-stroke': !isDragActive,
          },
          className
        )}
      >
        {children}

        <div
          className={cn('flex w-full flex-col items-center justify-center gap-2', {
            'scale-95 opacity-30': isDragActive,
          })}
        >
          <div className="flex flex-col items-center gap-1">
            {getMainIcon()}

            <p className="text-center text-sm leading-6 font-medium">
              {getMainText()}
              {shouldShowBrowseLink && (
                <span className="text-accent hover:underline"> {t('file_upload.browse')}</span>
              )}
            </p>
          </div>

          {!hasFiles && (
            <>
              {showAcceptedFormats && (
                <p className="text-center text-xs leading-4 text-foreground-muted">
                  {t('file_upload.supported_file_types', { 0: acceptedExtensions.join(', ') })}
                </p>
              )}
              <p className="text-center text-xs leading-4 text-foreground-muted">
                {t('file_upload.max_file_size', { 0: maxSizeMB })}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmptyState;
